// ==============================================
// 1. ИМПОРТЫ — подключаем нужные инструменты
// ==============================================

// Хуки React:
// - useEffect: выполняет код после рендера (например, меняет классы на странице)
// - useRef: даёт доступ к DOM-элементу кнопки напрямую
import { useEffect, useRef } from "react";

// Кастомный хук для работы с localStorage (сохраняет данные в браузере)
// Он сам обрабатывает чтение и запись, мы просто передаём ключ и значение
import { useLocalStorage } from "./../../utils/useLocalStorage";

// Иконки: солнце (светлая тема) и луна (тёмная тема)
// Они импортируются как компоненты/ссылки на файлы
import sun from "./sun.svg";
import moon from "./moon.svg";

// Стили для кнопки переключения тем
import "./style.css";

// Функция, которая определяет тему в системных настройках ОС
// Возвращает строку "dark" или "light"
import detectDarkMode from "../../utils/detectDarkMode";

// ==============================================
// 2. КОМПОНЕНТ — кнопка переключения темы
// ==============================================

const BtnDarkMode = () => {
  // ==============================================
  // 3. СОСТОЯНИЕ ТЕМЫ (хранится в localStorage)
  // ==============================================

  // darkMode: текущая тема ("dark" или "light")
  // setDarkMode: функция для изменения темы
  //
  // useLocalStorage работает так:
  // - Аргумент 1: ключ в localStorage (название записи)
  // - Аргумент 2: значение по умолчанию (если записи ещё нет)
  //
  // detectDarkMode() запускается один раз при создании компонента
  // и возвращает тему из настроек ОС пользователя
  //
  // Если в localStorage уже есть сохранённая тема, useLocalStorage
  // использует её, а не значение по умолчанию
  const [darkMode, setDarkMode] = useLocalStorage(
    "darkMode", // Ключ: будем хранить как "darkMode": "dark"
    detectDarkMode(), // Значение по умолчанию: определяем из ОС
  );

  // ==============================================
  // 4. ССЫЛКА НА КНОПКУ (для прямого доступа к DOM)
  // ==============================================

  // useRef создаёт объект { current: null }
  // Позже мы привяжем его к кнопке через атрибут ref
  // Это нужно, чтобы добавить/удалить класс у самой кнопки
  const btnRef = useRef(null);

  // ==============================================
  // 5. useEffect №1: ПРИМЕНЯЕМ ТЕМУ К СТРАНИЦЕ
  // ==============================================

  // Этот эффект срабатывает при КАЖДОМ изменении darkMode
  // и при первом рендере (так как в зависимостях [darkMode])
  useEffect(() => {
    // Проверяем: какая сейчас тема?
    if (darkMode === "dark") {
      // Если тёмная:
      // 1. Добавляем класс "dark" к тегу <body>
      //    CSS-стили с селектором body.dark изменят цвета на тёмные
      document.body.classList.add("dark");

      // 2. Добавляем класс активности к кнопке
      //    Обычно это анимация: сдвиг ползунка, подсветка и т.д.
      //    btnRef.current — это наш DOM-элемент кнопки
      btnRef.current.classList.add("dark-mode-btn--active");
    } else {
      // Если светлая (darkMode === "light"):
      // 1. Удаляем класс "dark" с body
      document.body.classList.remove("dark");

      // 2. Удаляем класс активности с кнопки
      btnRef.current.classList.remove("dark-mode-btn--active");
    }
  }, [darkMode]); // <-- ЗАВИСИМОСТЬ: если darkMode изменился — перезапускаем код

  // ==============================================
  // 6. useEffect №2: СЛЕДИМ ЗА СИСТЕМНЫМИ НАСТРОЙКАМИ
  // ==============================================

  // Этот эффект срабатывает ОДИН РАЗ при монтировании компонента
  // (пустой массив зависимостей [] означает "запустить только один раз")
  useEffect(() => {
    // matchMedia проверяет CSS-медиавыражение:
    // "(prefers-color-scheme: dark)" — выбрана ли тёмная тема в ОС
    window
      .matchMedia("(prefers-color-scheme: dark)")

      // addEventListener добавляет слушатель события "change"
      // Это событие срабатывает, когда пользователь меняет тему в ОС
      // (например, переключает в Windows или macOS)
      .addEventListener("change", (event) => {
        // event.matches — true, если выбрана тёмная тема, false — светлая
        // Создаём строку: "dark" или "light"
        const newColorScheme = event.matches ? "dark" : "light";

        // Обновляем состояние темы
        // Это вызовет useEffect №1, который применит тему к странице
        setDarkMode(newColorScheme);
      });
  }, []); // <-- ПУСТОЙ МАССИВ: эффект выполняется только один раз

  // ==============================================
  // 7. ФУНКЦИЯ ПЕРЕКЛЮЧЕНИЯ ТЕМЫ (вызывается по клику)
  // ==============================================

  const toggleDarkMode = () => {
    // setDarkMode может принимать функцию, которая получает текущее значение
    // Это гарантирует, что мы работаем с самым свежим состоянием
    setDarkMode((currentValue) => {
      // Если сейчас светлая тема — делаем тёмной
      // Если сейчас тёмная — делаем светлой
      return currentValue === "light" ? "dark" : "light";
    });
  };

  // ==============================================
  // 8. ВЕРСТКА КНОПКИ (JSX)
  // ==============================================

  return (
    // <></> — React Fragment (обёртка без лишнего тега в DOM)
    <>
      <button
        // ref={btnRef} — привязываем нашу ссылку к этому элементу
        // Теперь btnRef.current указывает на эту кнопку
        ref={btnRef}
        // Основной класс для кнопки (базовые стили: размер, фон, курсор)
        className="dark-mode-btn"
        // Обработчик клика — вызываем переключение темы
        onClick={toggleDarkMode}
      >
        {/* Иконка солнца (светлая тема) */}
        <img
          src={sun}
          alt="Light mode" // Альтернативный текст для доступности
          className="dark-mode-btn__icon"
        />

        {/* Иконка луны (тёмная тема) */}
        <img src={moon} alt="Dark mode" className="dark-mode-btn__icon" />
      </button>
    </>
  );
};

// ==============================================
// 9. ЭКСПОРТ — делаем компонент доступным для других файлов
// ==============================================

export default BtnDarkMode;
